import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useContractDraftingService from '../../app/services/hooks/useContractDraftingService';
import useHandleErrorFields from '../../hooks/useHandleErrorFields';
import InputField from '../../components/InputField';
import TextAreaField from '../../components/TextAreaField';
import SelectTwo from '../../components/SelectTwo';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import StepIndicator from '../../components/StepIndicator/StepIndicator';
import { AppContext } from '../../contexts/AppContext';

interface ContractSection {
    id: number | null;
    name: string;
    description: string;
    icon: string;
    order: number;
}

interface ContractField {
    id: number;
    label: string;
    placeholder_key: string;
    field_type: string;
    options: Array<{ value: string; label: string }>;
    default_value: string;
    is_required: boolean;
    help_text: string;
    section_name: string;
    section_id: number | null;
}

interface ContractArticleOption {
    id: number;
    title: string;
    content: string;
    order: number;
}

interface ContractArticle {
    id: number;
    title: string;
    content: string;
    is_optional: boolean;
    options: ContractArticleOption[];
}

interface ContractType {
    id: number;
    name: string;
    price: number;
}

interface FormData {
    [key: string]: any;
}

const ContractRequestForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { getFormConfig, saveFormData, fetchingFormConfig, fetchingSaveFormData } = useContractDraftingService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    
    const [sections, setSections] = useState<ContractSection[]>([]);
    const [fields, setFields] = useState<ContractField[]>([]);
    const [articles, setArticles] = useState<ContractArticle[]>([]);
    const [formData, setFormData] = useState<FormData>({});
    const [selectedArticleOptions, setSelectedArticleOptions] = useState<{[articleId: number]: number}>({});
    const [contractType, setContractType] = useState<ContractType | null>(null);
    const [currentStep, setCurrentStep] = useState(0);

    useEffect(() => {
        loadFormConfig();
    }, []);

    useEffect(() => {
        if (fetchingFormConfig || fetchingSaveFormData) {
            showLoading?.();
        } else {
            hideLoading?.();
        }
    }, [fetchingFormConfig, fetchingSaveFormData]);

    const loadFormConfig = () => {
        getFormConfig(parseInt(id), {
            onSuccess: (res) => {
                const sectionsData = res.data.sections || [];
                const fieldsData = res.data.fields || [];
                const articlesData = res.data.articles || [];
                
                // Hay tipos de contrato que no piden nada al cliente: su plantilla viene
                // completa. Sin campos, secciones ni cláusulas el formulario no aporta un
                // paso, así que se pasa directo al preview.
                if (fieldsData.length === 0 && sectionsData.length === 0 && articlesData.length === 0) {
                    history.replace(`/intranet/contracts/${id}/preview`);
                    return;
                }
                
                setSections(sectionsData);
                setFields(fieldsData);
                setArticles(articlesData);
                setContractType(res.data.contract_type || null);
                
                // Cargar datos guardados previamente (si existen)
                const savedFormData = res.data.form_data || {};
                const savedArticleOptions = res.data.selected_article_options || [];
                
                // Inicializar form data con valores guardados o valores por defecto
                const initialData: FormData = {};
                res.data.fields.forEach((field: ContractField) => {
                    // Priorizar datos guardados, luego valores por defecto
                    if (savedFormData[field.placeholder_key]) {
                        initialData[field.placeholder_key] = savedFormData[field.placeholder_key];
                    } else if (field.default_value) {
                        initialData[field.placeholder_key] = field.default_value;
                    }
                });
                setFormData(initialData);
                
                // Reconstruir selectedArticleOptions desde array de IDs
                // Necesitamos mapear cada option_id a su article_id correspondiente
                const articleOptionsMap: {[articleId: number]: number} = {};
                res.data.articles.forEach((article: ContractArticle) => {
                    article.options.forEach((option: ContractArticleOption) => {
                        if (savedArticleOptions.includes(option.id)) {
                            articleOptionsMap[article.id] = option.id;
                        }
                    });
                });
                setSelectedArticleOptions(articleOptionsMap);
            },
            onError: (res) => {
                toast.error(res.message || 'Error al cargar el formulario');
                history.push('/intranet/contracts/new');
            }
        });
    };

    const handleInputChange = (fieldKey: string, value: any) => {
        setFormData({ ...formData, [fieldKey]: value });
    };

    const handleArticleOptionSelect = (articleId: number, optionId: number) => {
        setSelectedArticleOptions({
            ...selectedArticleOptions,
            [articleId]: optionId
        });
    };

    // Validar paso actual antes de avanzar
    const validateCurrentStep = (): boolean => {
        const currentSection = sections[currentStep];
        if (!currentSection) return true;
        
        // Obtener campos de la sección actual
        const sectionFields = fields.filter(field => {
            if (currentSection.id === null) {
                return field.section_id === null;
            }
            return field.section_id === currentSection.id;
        });
        
        // Validar campos requeridos de la sección actual
        const errors: {[key: string]: string[]} = {};
        let hasErrors = false;
        
        sectionFields.forEach(field => {
            if (field.is_required) {
                const value = formData[field.placeholder_key];
                if (!value || (typeof value === 'string' && value.trim() === '')) {
                    errors[`form_data.${field.placeholder_key}`] = [`El campo ${field.label} es requerido.`];
                    hasErrors = true;
                }
            }
        });
        
        if (hasErrors) {
            setErrors(errors);
            toast.error('Por favor completa todos los campos requeridos antes de continuar');
            return false;
        }
        
        return true;
    };

    const handleNext = () => {
        if (!validateCurrentStep()) {
            return;
        }
        
        // Si no es el último paso, avanzar
        const totalSteps = sections.length + (articles.length > 0 ? 1 : 0); // Secciones + paso de artículos
        if (currentStep < totalSteps - 1) {
            setErrors({});
            setCurrentStep(currentStep + 1);
        } else {
            // Es el último paso, guardar y continuar
            handleSubmit();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setErrors({});
            setCurrentStep(currentStep - 1);
        } else {
            history.push('/intranet/contracts/new');
        }
    };

    const handleSubmit = () => {
        // Validar último paso (artículos si existen)
        if (articles.length > 0 && currentStep === sections.length) {
            // Validar artículos requeridos
            const requiredArticles = articles.filter(article => !article.is_optional);
            const missingArticles = requiredArticles.filter(article => !selectedArticleOptions[article.id]);
            
            if (missingArticles.length > 0) {
                toast.error('Por favor selecciona una opción para todas las cláusulas requeridas');
                return;
            }
        }
        
        // Convertir selectedArticleOptions a array de option_ids
        const selected_article_options = Object.values(selectedArticleOptions).filter(id => id !== undefined);
        
        const payload = {
            form_data: formData,
            selected_article_options: selected_article_options
        };
        
        saveFormData(parseInt(id), payload, {
            onSuccess: (res) => {
                toast.success('Datos guardados exitosamente');
                setErrors({});
                // Redirigir al preview donde se genera el documento
                history.push(`/intranet/contracts/${id}/preview`);
            },
            onError: (res) => {
                toast.error(res.message || 'Error al guardar los datos');
            },
            onFieldError: (res) => {
                setErrors(res.data);
                toast.error('Por favor corrige los errores en el formulario');
            }
        });
    };

    const renderField = (field: ContractField) => {
        const commonProps = {
            label: field.label,
            value: formData[field.placeholder_key] || '',
            onChange: (value: string) => handleInputChange(field.placeholder_key, value),
            onFocus: () => onFocusRemove(`form_data.${field.placeholder_key}`),
            error: fieldErrorMessage(`form_data.${field.placeholder_key}`),
            required: field.is_required,
            placeholder: field.help_text || `Ingrese ${field.label.toLowerCase()}`
        };

        switch (field.field_type) {
            case 'textarea':
                return <TextAreaField key={field.id} {...commonProps} rows={4} />;
            
            case 'select':
                return (
                    <SelectTwo
                        key={field.id}
                        label={field.label}
                        inputValue={formData[field.placeholder_key] || ''}
                        onChange={(option: any) => handleInputChange(field.placeholder_key, option?.value || '')}
                        options={field.options}
                        isClearable
                    />
                );
            
            case 'date':
            case 'number':
            case 'email':
                return <InputField key={field.id} {...commonProps} type={field.field_type} />;
            
            case 'text':
            case 'rut':
            case 'currency':
            default:
                return <InputField key={field.id} {...commonProps} type="text" />;
        }
    };

    // Construir pasos para el indicador
    const buildSteps = () => {
        const steps: Array<{ number: number; title: string; status: 'completed' | 'current' | 'pending' }> = [];
        
        // Agregar pasos de secciones
        sections.forEach((section, index) => {
            steps.push({
                number: index + 1,
                title: section.name,
                status: index < currentStep ? 'completed' : index === currentStep ? 'current' : 'pending'
            });
        });
        
        // Agregar paso de artículos si existen
        if (articles.length > 0) {
            const articleStepIndex = sections.length;
            steps.push({
                number: articleStepIndex + 1,
                title: 'Cláusulas',
                status: articleStepIndex < currentStep ? 'completed' : articleStepIndex === currentStep ? 'current' : 'pending'
            });
        }
        
        return steps;
    };

    // Renderizar contenido del paso actual
    const renderCurrentStep = () => {
        const totalSteps = sections.length + (articles.length > 0 ? 1 : 0);
        
        // Si es un paso de sección
        if (currentStep < sections.length) {
            const currentSection = sections[currentStep];
            const sectionFields = fields.filter(field => {
                if (currentSection.id === null) {
                    return field.section_id === null;
                }
                return field.section_id === currentSection.id;
            });
            
            return (
                <div>
                    <div className="mb-4">
                        <h4 className="mb-2">
                            {currentSection.icon && <i className={`${currentSection.icon} me-2 text-primary`}></i>}
                            {currentSection.name}
                        </h4>
                        {currentSection.description && (
                            <p className="text-muted mb-0">{currentSection.description}</p>
                        )}
                    </div>
                    
                    {sectionFields.length === 0 ? (
                        <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            Esta sección no tiene campos configurados.
                        </div>
                    ) : (
                        <div className="row">
                            {sectionFields.map((field) => (
                                <div key={field.id} className="col-md-6 mb-3">
                                    {renderField(field)}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }
        
        // Si es el paso de artículos
        if (currentStep === sections.length && articles.length > 0) {
            return (
                <div>
                    <div className="mb-4">
                        <h4 className="mb-2">
                            <i className="fas fa-list-ul me-2 text-primary"></i>
                            Cláusulas
                        </h4>
                        <p className="text-muted mb-0">
                            Selecciona una alternativa para cada cláusula
                        </p>
                    </div>
                    
                    {articles.map((article) => (
                        <div key={article.id} className="card mb-3">
                            <div className="card-body">
                                <h6 className="mb-3">
                                    {article.title}
                                    {!article.is_optional && (
                                        <span className="badge bg-danger ms-2">Requerido</span>
                                    )}
                                </h6>
                                
                                {article.options.length > 0 ? (
                                    <div>
                                        {article.options.map((option) => (
                                            <div key={option.id} className="form-check mb-2">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name={`article-${article.id}`}
                                                    id={`option-${option.id}`}
                                                    checked={selectedArticleOptions[article.id] === option.id}
                                                    onChange={() => handleArticleOptionSelect(article.id, option.id)}
                                                />
                                                <label className="form-check-label" htmlFor={`option-${option.id}`}>
                                                    <strong>{option.title}</strong>
                                                    <p className="text-muted mb-0 mt-1" style={{ fontSize: '14px' }}>
                                                        {option.content}
                                                    </p>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="alert alert-warning mb-0">
                                        Este artículo no tiene alternativas configuradas
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }
        
        return null;
    };

    const steps = buildSteps();
    const totalSteps = sections.length + (articles.length > 0 ? 1 : 0);
    const progress = totalSteps > 0 ? Math.round(((currentStep + 1) / totalSteps) * 100) : 0;

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="mb-4">
                                <h4 className="mb-1">📝 {contractType?.name || 'Cargando...'}</h4>
                                <p className="text-muted mb-0">Completa los datos del contrato paso a paso</p>
                            </div>

                            {/* Indicador de pasos */}
                            {steps.length > 0 && (
                                <div className="mb-4">
                                    <StepIndicator
                                        steps={steps}
                                        currentStep={currentStep + 1}
                                        onStepClick={(stepNumber) => {
                                            // Solo permitir navegar a pasos completados o actual
                                            const stepIndex = stepNumber - 1;
                                            if (stepIndex <= currentStep || stepIndex === currentStep + 1) {
                                                setCurrentStep(stepIndex);
                                                setErrors({});
                                            }
                                        }}
                                    />
                                    
                                    {/* Barra de progreso */}
                                    <div className="mt-3">
                                        <div className="progress" style={{ height: '8px' }}>
                                            <div
                                                className="progress-bar bg-primary"
                                                role="progressbar"
                                                style={{ width: `${progress}%` }}
                                                aria-valuenow={progress}
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                            ></div>
                                        </div>
                                        <small className="text-muted mt-1 d-block">
                                            Paso {currentStep + 1} de {totalSteps} ({progress}%)
                                        </small>
                                    </div>
                                </div>
                            )}

                            <div>
                                {/* Contenido del paso actual */}
                                {renderCurrentStep()}

                                {/* Botones de navegación */}
                                <div className="d-flex justify-content-between mt-4">
                                    <ButtonGeneral
                                        type="button"
                                        variant="outline"
                                        title={currentStep === 0 ? 'Volver' : 'Anterior'}
                                        onClick={handleBack}
                                        icon={currentStep === 0 ? 'fa-arrow-left' : 'fa-arrow-left'}
                                    />
                                    <ButtonGeneral
                                        type="button"
                                        variant="primary"
                                        title={currentStep === totalSteps - 1 ? 'Guardar y Continuar' : 'Siguiente'}
                                        onClick={handleNext}
                                        loading={fetchingSaveFormData}
                                        icon={currentStep === totalSteps - 1 ? 'fa-arrow-right' : 'fa-arrow-right'}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ContractRequestForm;
